type CompanyRole = 'owner' | 'manager' | 'employee'

type DeptRole = 'department_leader' | 'team_leader' | null

type RoleId = CompanyRole | Exclude<DeptRole, null>

type PermissionKey =
	| 'hr.members.view'
	| 'hr.members.manage'
	| 'hr.members.edit-own'
	| 'hr.time-off.view-own'
	| 'hr.time-off.view-dept'
	| 'hr.time-off.view-team'
	| 'hr.time-off.view-all'
	| 'hr.time-off.request'
	| 'hr.time-off.approve-dept'
	| 'hr.time-off.approve-hr'
	| 'hr.time-off.notes'
	| 'workflows.view'
	| 'workflows.manage'
	| 'settings.permissions.manage'

type ResolvedPermissions = Record<PermissionKey, boolean>

type UserContext = {
	userId: string
	companyRole: CompanyRole
	deptRole: DeptRole
	departmentId: string | null
	teamIds: string[]
}

// Base grants per role, configurable by admins
type RolePermissionConfig = {
	roleId: RoleId
	grants: PermissionKey[]
}

// Additional grants for a specific role scoped to a department or team
type ScopedPermissionConfig = {
	scopeType: 'department' | 'team'
	scopeId: string
	roleId: RoleId
	grants: PermissionKey[]
}

// Per-user fine-grained control on top of resolved role+scope grants
type UserPermissionOverride = {
	userId: string
	additions: PermissionKey[]
	revocations: PermissionKey[]
}

// The full admin-editable configuration
type PermissionConfiguration = {
	roleConfigs: RolePermissionConfig[]
	scopedConfigs: ScopedPermissionConfig[]
	userOverrides: UserPermissionOverride[]
}

type PermissionUser = {
	id: string
	fullName: string | null
	email: string
}

type PermissionPort = {
	getConfig(): Promise<PermissionConfiguration>
	saveConfig(config: PermissionConfiguration): Promise<void>
	listUsers(): Promise<PermissionUser[]>
}

const PERMISSIONS_DENIED: ResolvedPermissions = {
	'hr.members.view': false,
	'hr.members.manage': false,
	'hr.members.edit-own': false,
	'hr.time-off.view-own': false,
	'hr.time-off.view-dept': false,
	'hr.time-off.view-team': false,
	'hr.time-off.view-all': false,
	'hr.time-off.request': false,
	'hr.time-off.approve-dept': false,
	'hr.time-off.approve-hr': false,
	'hr.time-off.notes': false,
	'workflows.view': false,
	'workflows.manage': false,
	'settings.permissions.manage': false,
}

const DEFAULT_PERMISSION_CONFIG: PermissionConfiguration = {
	roleConfigs: [
		{
			roleId: 'employee',
			grants: [
				'hr.members.view',
				'hr.members.edit-own',
				'hr.time-off.view-own',
				'hr.time-off.request',
				'workflows.view',
			],
		},
		{
			roleId: 'manager',
			grants: [
				'hr.members.view',
				'hr.members.manage',
				'hr.time-off.view-dept',
				'hr.time-off.view-team',
				'hr.time-off.view-all',
				'hr.time-off.approve-dept',
				'hr.time-off.approve-hr',
				'hr.time-off.notes',
				'workflows.manage',
				'settings.permissions.manage',
			],
		},
		{
			roleId: 'owner',
			grants: [
				'hr.members.view',
				'hr.members.manage',
				'hr.time-off.view-dept',
				'hr.time-off.view-team',
				'hr.time-off.view-all',
				'hr.time-off.approve-dept',
				'hr.time-off.approve-hr',
				'hr.time-off.notes',
				'workflows.manage',
				'settings.permissions.manage',
			],
		},
		{
			roleId: 'department_leader',
			grants: ['hr.time-off.view-dept', 'hr.time-off.approve-dept', 'hr.time-off.notes'],
		},
		{
			roleId: 'team_leader',
			grants: ['hr.time-off.view-team', 'hr.time-off.notes'],
		},
	],
	scopedConfigs: [],
	userOverrides: [],
}

// Resolution order:
// 1. Employee base
// 2. companyRole grants
// 3. deptRole grants
// 4. Scoped grants for user's department matching any of their roles
// 5. Scoped grants for user's teams matching any of their roles
// 6. User-level additions
// 7. User-level revocations (applied last so they always win)
const computePermissions = (config: PermissionConfiguration, ctx: UserContext): ResolvedPermissions => {
	const grants = new Set<PermissionKey>()
	const roleConfigs = config.roleConfigs ?? []
	const scopedConfigs = config.scopedConfigs ?? []
	const userOverrides = config.userOverrides ?? []

	const userRoles: RoleId[] = [ctx.companyRole]
	if (ctx.deptRole) userRoles.push(ctx.deptRole)

	// Role-level grants
	for (const k of roleConfigs.find(c => c.roleId === 'employee')?.grants ?? []) grants.add(k)
	if (ctx.companyRole !== 'employee') {
		for (const k of roleConfigs.find(c => c.roleId === ctx.companyRole)?.grants ?? []) grants.add(k)
	}
	if (ctx.deptRole) {
		for (const k of roleConfigs.find(c => c.roleId === ctx.deptRole)?.grants ?? []) grants.add(k)
	}

	// Scoped grants - department
	if (ctx.departmentId) {
		for (const s of scopedConfigs.filter(
			s => s.scopeType === 'department' && s.scopeId === ctx.departmentId && userRoles.includes(s.roleId),
		)) {
			for (const k of s.grants) grants.add(k)
		}
	}

	// Scoped grants - teams
	if (ctx.teamIds.length > 0) {
		for (const s of scopedConfigs.filter(
			s => s.scopeType === 'team' && ctx.teamIds.includes(s.scopeId) && userRoles.includes(s.roleId),
		)) {
			for (const k of s.grants) grants.add(k)
		}
	}

	const userOverride = userOverrides.find(o => o.userId === ctx.userId)
	if (userOverride) {
		for (const k of userOverride.additions) grants.add(k)
		for (const k of userOverride.revocations) grants.delete(k)
	}

	return Object.fromEntries(
		(Object.keys(PERMISSIONS_DENIED) as PermissionKey[]).map(k => [k, grants.has(k)]),
	) as ResolvedPermissions
}

export type {
	CompanyRole,
	DeptRole,
	RoleId,
	PermissionKey,
	ResolvedPermissions,
	UserContext,
	RolePermissionConfig,
	ScopedPermissionConfig,
	UserPermissionOverride,
	PermissionConfiguration,
	PermissionUser,
	PermissionPort,
}
export { PERMISSIONS_DENIED, DEFAULT_PERMISSION_CONFIG, computePermissions }
